// Display names (users/{uid}/name), fetched only for the people on screen and
// cached for the session, instead of downloading every user.

import { useEffect, useState } from 'react';
import { getDatabase, ref, get } from 'firebase/database';

const cache = new Map();   // uid -> name ('' if none)
const pending = new Map(); // uid -> Promise

function load(uid) {
  if (cache.has(uid)) return Promise.resolve(cache.get(uid));
  if (!pending.has(uid)) {
    pending.set(uid, get(ref(getDatabase(), `users/${uid}`))
      .then(s => { const u = s.val() || {}; return (u.name || u.username || '').trim(); })
      .catch(() => '')
      .then(name => { cache.set(uid, name); pending.delete(uid); return name; }));
  }
  return pending.get(uid);
}

/** { uid: name } for the given user IDs; fills in as names arrive. */
export function useNames(uids) {
  const key = [...new Set(uids.filter(Boolean))].sort().join(',');
  const [names, setNames] = useState(() => Object.fromEntries(
    key ? key.split(',').filter(u => cache.has(u)).map(u => [u, cache.get(u)]) : []));
  useEffect(() => {
    if (!key) return;
    let live = true;
    Promise.all(key.split(',').map(uid => load(uid).then(n => [uid, n])))
      .then(pairs => { if (live) setNames(prev => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { live = false; };
  }, [key]);
  return names;
}

export const firstNameOf = name => (name || '').trim().split(/\s+/)[0] || '';
export function initialsOf(name) {
  const p = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return (p.length >= 2 ? p[0][0] + p[p.length - 1][0] : p[0].slice(0, 2)).toUpperCase();
}
